import React, { useState } from 'react';
import {
  MessageCircle, Brain, GitBranch, Send, Webhook, Clock, Sparkles, Tag, Database,
  Mail, Phone, Globe, Calendar, FileText, Image, Video, Music, Upload, Download,
  DollarSign, ShoppingCart, CreditCard, Package, Truck, Users, UserPlus, UserCheck,
  Settings, Zap, Filter, Shuffle, Copy, Trash, Edit, Eye, Lock, Unlock,
  BarChart, TrendingUp, PieChart, Activity, Target, Award, Star, Heart,
  Facebook, Twitter, Instagram, Linkedin, Youtube, Search, Link, Share2,
  Cloud, Server, Cpu, HardDrive, Folder, File, Code, Terminal,
  Repeat, ArrowRight, ArrowDown, ArrowUp, Plus, Minus, X, Check,
  Bell, AlertCircle, Info, HelpCircle, Bookmark, Flag, Map, MapPin,
  ShoppingBag, Store, Gift, Receipt, Percent, TrendingDown
} from 'lucide-react';

import { i18n } from "../../translate/i18n";

// Paleta de colores profesional inspirada en Three.js y herramientas modernas
const professionalColors = {
  trigger: { primary: '#10b981', secondary: '#059669', light: '#d1fae5' },
  ai: { primary: '#8b5cf6', secondary: '#7c3aed', light: '#ede9fe' },
  crm: { primary: '#3b82f6', secondary: '#2563eb', light: '#dbeafe' },
  ecommerce: { primary: '#f59e0b', secondary: '#d97706', light: '#fef3c7' },
  communication: { primary: '#06b6d4', secondary: '#0891b2', light: '#cffafe' },
  database: { primary: '#6366f1', secondary: '#4f46e5', light: '#e0e7ff' },
  storage: { primary: '#ec4899', secondary: '#db2777', light: '#fce7f3' },
  analytics: { primary: '#14b8a6', secondary: '#0d9488', light: '#ccfbf1' },
  marketing: { primary: '#f97316', secondary: '#ea580c', light: '#ffedd5' },
  logic: { primary: '#0ea5e9', secondary: '#0284c7', light: '#e0f2fe' },
  transform: { primary: '#a855f7', secondary: '#9333ea', light: '#f3e8ff' },
  action: { primary: '#ef4444', secondary: '#dc2626', light: '#fee2e2' }
};

const nodeCategories = [
  {
    id: 'trigger',
    name: 'Triggers',
    color: professionalColors.trigger.primary,
    description: 'Inicia tu flujo automáticamente',
    nodes: [
      { type: 'trigger', subtype: 'whatsapp_message', label: 'WhatsApp Message', icon: MessageCircle },
      { type: 'trigger', subtype: 'whatsapp_status', label: 'WhatsApp Status', icon: Eye },
      { type: 'trigger', subtype: 'telegram_message', label: 'Telegram Message', icon: Send },
      { type: 'trigger', subtype: 'email_received', label: 'Email Received', icon: Mail },
      { type: 'trigger', subtype: 'sms_received', label: 'SMS Received', icon: Phone },
      { type: 'trigger', subtype: 'webhook', label: 'Webhook', icon: Webhook },
      { type: 'trigger', subtype: 'schedule', label: 'Schedule/Cron', icon: Clock },
      { type: 'trigger', subtype: 'form_submit', label: 'Form Submit', icon: FileText },
      { type: 'trigger', subtype: 'file_upload', label: 'File Upload', icon: Upload },
      { type: 'trigger', subtype: 'api_call', label: 'API Call', icon: Globe },
      { type: 'trigger', subtype: 'database_change', label: 'Database Change', icon: Database },
      { type: 'trigger', subtype: 'payment_received', label: 'Payment Received', icon: DollarSign },
      { type: 'trigger', subtype: 'order_placed', label: 'Order Placed', icon: ShoppingCart },
      { type: 'trigger', subtype: 'new_contact', label: 'New Contact', icon: UserPlus },
      { type: 'trigger', subtype: 'calendar_event', label: 'Calendar Event', icon: Calendar },
      { type: 'trigger', subtype: 'button_click', label: 'Button Click', icon: Zap },
      { type: 'trigger', subtype: 'qr_scan', label: 'QR Scan', icon: Search },
      { type: 'trigger', subtype: 'voice_command', label: 'Voice Command', icon: Music }
    ]
  },
  {
    id: 'ai',
    name: 'AI & Machine Learning',
    color: professionalColors.ai.primary,
    description: 'Inteligencia artificial avanzada',
    nodes: [
      { type: 'ai', subtype: 'gpt4', label: 'GPT-4 Chat', icon: Brain, config: { model: 'gpt-4-turbo', temperature: 0.7 } },
      { type: 'ai', subtype: 'gpt4_vision', label: 'GPT-4 Vision', icon: Eye },
      { type: 'ai', subtype: 'claude', label: 'Claude 3', icon: Brain },
      { type: 'ai', subtype: 'gemini', label: 'Google Gemini', icon: Sparkles },
      { type: 'ai', subtype: 'dall_e', label: 'DALL-E Image', icon: Image },
      { type: 'ai', subtype: 'whisper', label: 'Whisper Audio', icon: Music },
      { type: 'ai', subtype: 'tts', label: 'Text to Speech', icon: Music },
      { type: 'ai', subtype: 'intent_detection', label: 'Intent Detection', icon: Target },
      { type: 'ai', subtype: 'sentiment', label: 'Sentiment Analysis', icon: Heart },
      { type: 'ai', subtype: 'rag', label: 'RAG Query', icon: Database },
      { type: 'ai', subtype: 'summarize', label: 'Text Summarization', icon: FileText },
      { type: 'ai', subtype: 'translate', label: 'Translation', icon: Globe },
      { type: 'ai', subtype: 'classify', label: 'Text Classification', icon: Tag },
      { type: 'ai', subtype: 'entity_extraction', label: 'Entity Extraction', icon: Search },
      { type: 'ai', subtype: 'embeddings', label: 'Generate Embeddings', icon: Code },
      { type: 'ai', subtype: 'question_answer', label: 'Q&A System', icon: HelpCircle },
      { type: 'ai', subtype: 'content_moderate', label: 'Content Moderation', icon: AlertCircle },
      { type: 'ai', subtype: 'image_recognition', label: 'Image Recognition', icon: Eye },
      { type: 'ai', subtype: 'ocr', label: 'OCR Text Extract', icon: FileText },
      { type: 'ai', subtype: 'face_detection', label: 'Face Detection', icon: Users },
      { type: 'ai', subtype: 'object_detection', label: 'Object Detection', icon: Search },
      { type: 'ai', subtype: 'speech_recognition', label: 'Speech Recognition', icon: Music },
      { type: 'ai', subtype: 'video_analysis', label: 'Video Analysis', icon: Video },
      { type: 'ai', subtype: 'predictive', label: 'Predictive Model', icon: TrendingUp },
      { type: 'ai', subtype: 'recommendation', label: 'Recommendation Engine', icon: Star }
    ]
  },
  {
    id: 'crm',
    name: 'CRM & Sales',
    color: professionalColors.crm.primary,
    description: 'Gestión de clientes y ventas',
    nodes: [
      { type: 'crm', subtype: 'salesforce_lead', label: 'Salesforce Lead', icon: Users },
      { type: 'crm', subtype: 'salesforce_contact', label: 'Salesforce Contact', icon: UserCheck },
      { type: 'crm', subtype: 'hubspot_contact', label: 'HubSpot Contact', icon: Users },
      { type: 'crm', subtype: 'hubspot_deal', label: 'HubSpot Deal', icon: DollarSign },
      { type: 'crm', subtype: 'pipedrive_deal', label: 'Pipedrive Deal', icon: TrendingUp },
      { type: 'crm', subtype: 'pipedrive_activity', label: 'Pipedrive Activity', icon: Activity },
      { type: 'crm', subtype: 'zoho_lead', label: 'Zoho Lead', icon: UserPlus },
      { type: 'crm', subtype: 'zoho_account', label: 'Zoho Account', icon: Users },
      { type: 'crm', subtype: 'dynamics_contact', label: 'Dynamics Contact', icon: UserCheck },
      { type: 'crm', subtype: 'freshsales', label: 'Freshsales', icon: Users },
      { type: 'crm', subtype: 'airtable', label: 'Airtable', icon: Database },
      { type: 'crm', subtype: 'notion', label: 'Notion Database', icon: FileText },
      { type: 'crm', subtype: 'monday', label: 'Monday.com', icon: Calendar },
      { type: 'crm', subtype: 'asana', label: 'Asana Task', icon: Check },
      { type: 'crm', subtype: 'trello', label: 'Trello Card', icon: Bookmark },
      { type: 'crm', subtype: 'jira', label: 'Jira Issue', icon: Flag },
      { type: 'crm', subtype: 'zendesk', label: 'Zendesk Ticket', icon: HelpCircle },
      { type: 'crm', subtype: 'intercom', label: 'Intercom Message', icon: MessageCircle }
    ]
  },
  {
    id: 'ecommerce',
    name: 'E-commerce & Payments',
    color: professionalColors.ecommerce.primary,
    description: 'Tiendas online y pagos',
    nodes: [
      { type: 'ecommerce', subtype: 'shopify_order', label: 'Shopify Order', icon: ShoppingCart },
      { type: 'ecommerce', subtype: 'shopify_product', label: 'Shopify Product', icon: Package },
      { type: 'ecommerce', subtype: 'shopify_customer', label: 'Shopify Customer', icon: Users },
      { type: 'ecommerce', subtype: 'woocommerce_order', label: 'WooCommerce Order', icon: ShoppingBag },
      { type: 'ecommerce', subtype: 'woocommerce_product', label: 'WooCommerce Product', icon: Store },
      { type: 'ecommerce', subtype: 'mercadolibre', label: 'Mercado Libre', icon: ShoppingCart },
      { type: 'ecommerce', subtype: 'amazon', label: 'Amazon FBA', icon: Package },
      { type: 'ecommerce', subtype: 'ebay', label: 'eBay Listing', icon: Tag },
      { type: 'ecommerce', subtype: 'stripe_payment', label: 'Stripe Payment', icon: CreditCard },
      { type: 'ecommerce', subtype: 'stripe_subscription', label: 'Stripe Subscription', icon: Repeat },
      { type: 'ecommerce', subtype: 'paypal', label: 'PayPal Transaction', icon: DollarSign },
      { type: 'ecommerce', subtype: 'square', label: 'Square Payment', icon: CreditCard },
      { type: 'ecommerce', subtype: 'mercadopago', label: 'Mercado Pago', icon: DollarSign },
      { type: 'ecommerce', subtype: 'conekta', label: 'Conekta Payment', icon: CreditCard },
      { type: 'ecommerce', subtype: 'wix_store', label: 'Wix Store', icon: Store },
      { type: 'ecommerce', subtype: 'squarespace', label: 'Squarespace', icon: Store },
      { type: 'ecommerce', subtype: 'bigcommerce', label: 'BigCommerce', icon: ShoppingCart },
      { type: 'ecommerce', subtype: 'magento', label: 'Magento', icon: Store },
      { type: 'ecommerce', subtype: 'prestashop', label: 'PrestaShop', icon: ShoppingBag },
      { type: 'ecommerce', subtype: 'shipment_tracking', label: 'Shipment Tracking', icon: Truck }
    ]
  },
  {
    id: 'communication',
    name: 'Communication',
    color: professionalColors.communication.primary,
    description: 'Mensajería y comunicación',
    nodes: [
      { type: 'communication', subtype: 'whatsapp_send', label: 'WhatsApp Send', icon: MessageCircle },
      { type: 'communication', subtype: 'whatsapp_template', label: 'WhatsApp Template', icon: FileText },
      { type: 'communication', subtype: 'whatsapp_media', label: 'WhatsApp Media', icon: Image },
      { type: 'communication', subtype: 'telegram_send', label: 'Telegram Send', icon: Send },
      { type: 'communication', subtype: 'telegram_bot', label: 'Telegram Bot', icon: Send },
      { type: 'communication', subtype: 'slack_message', label: 'Slack Message', icon: MessageCircle },
      { type: 'communication', subtype: 'slack_channel', label: 'Slack Channel', icon: Users },
      { type: 'communication', subtype: 'discord_message', label: 'Discord Message', icon: MessageCircle },
      { type: 'communication', subtype: 'discord_embed', label: 'Discord Embed', icon: FileText },
      { type: 'communication', subtype: 'email_smtp', label: 'Email SMTP', icon: Mail },
      { type: 'communication', subtype: 'email_template', label: 'Email Template', icon: FileText },
      { type: 'communication', subtype: 'sms_twilio', label: 'SMS Twilio', icon: Phone },
      { type: 'communication', subtype: 'sms_nexmo', label: 'SMS Nexmo', icon: Phone },
      { type: 'communication', subtype: 'voice_call', label: 'Voice Call', icon: Phone },
      { type: 'communication', subtype: 'push_notification', label: 'Push Notification', icon: Bell },
      { type: 'communication', subtype: 'teams_message', label: 'Microsoft Teams', icon: MessageCircle },
      { type: 'communication', subtype: 'zoom_meeting', label: 'Zoom Meeting', icon: Video },
      { type: 'communication', subtype: 'google_meet', label: 'Google Meet', icon: Video },
      { type: 'communication', subtype: 'messenger_send', label: 'Facebook Messenger', icon: MessageCircle },
      { type: 'communication', subtype: 'instagram_dm', label: 'Instagram DM', icon: Instagram },
      { type: 'communication', subtype: 'twitter_dm', label: 'Twitter DM', icon: Twitter },
      { type: 'communication', subtype: 'linkedin_message', label: 'LinkedIn Message', icon: Linkedin }
    ]
  },
  {
    id: 'database',
    name: 'Databases & Storage',
    color: professionalColors.database.primary,
    description: 'Bases de datos y almacenamiento',
    nodes: [
      { type: 'database', subtype: 'postgresql', label: 'PostgreSQL', icon: Database },
      { type: 'database', subtype: 'mysql', label: 'MySQL', icon: Database },
      { type: 'database', subtype: 'mongodb', label: 'MongoDB', icon: Database },
      { type: 'database', subtype: 'redis', label: 'Redis Cache', icon: Zap },
      { type: 'database', subtype: 'elasticsearch', label: 'Elasticsearch', icon: Search },
      { type: 'database', subtype: 'firebase_firestore', label: 'Firebase Firestore', icon: Database },
      { type: 'database', subtype: 'firebase_realtime', label: 'Firebase Realtime', icon: Activity },
      { type: 'database', subtype: 'supabase', label: 'Supabase', icon: Database },
      { type: 'database', subtype: 'dynamodb', label: 'AWS DynamoDB', icon: Database },
      { type: 'database', subtype: 'cosmosdb', label: 'Azure Cosmos DB', icon: Database },
      { type: 'database', subtype: 'cassandra', label: 'Cassandra', icon: Database },
      { type: 'database', subtype: 'mariadb', label: 'MariaDB', icon: Database },
      { type: 'database', subtype: 'oracle', label: 'Oracle DB', icon: Database },
      { type: 'database', subtype: 'sql_server', label: 'SQL Server', icon: Database },
      { type: 'database', subtype: 'sqlite', label: 'SQLite', icon: Database },
      { type: 'database', subtype: 'couchdb', label: 'CouchDB', icon: Database },
      { type: 'database', subtype: 'influxdb', label: 'InfluxDB', icon: TrendingUp },
      { type: 'database', subtype: 'neo4j', label: 'Neo4j Graph', icon: GitBranch }
    ]
  },
  {
    id: 'storage',
    name: 'Cloud & File Storage',
    color: professionalColors.storage.primary,
    description: 'Almacenamiento en la nube',
    nodes: [
      { type: 'storage', subtype: 'google_drive', label: 'Google Drive', icon: Cloud },
      { type: 'storage', subtype: 'google_sheets', label: 'Google Sheets', icon: FileText },
      { type: 'storage', subtype: 'dropbox', label: 'Dropbox', icon: Cloud },
      { type: 'storage', subtype: 's3', label: 'AWS S3', icon: Cloud },
      { type: 'storage', subtype: 'azure_blob', label: 'Azure Blob Storage', icon: Cloud },
      { type: 'storage', subtype: 'onedrive', label: 'OneDrive', icon: Cloud },
      { type: 'storage', subtype: 'box', label: 'Box Storage', icon: Cloud },
      { type: 'storage', subtype: 'ftp', label: 'FTP Server', icon: Server },
      { type: 'storage', subtype: 'sftp', label: 'SFTP Server', icon: Server },
      { type: 'storage', subtype: 'github', label: 'GitHub Repo', icon: Code },
      { type: 'storage', subtype: 'gitlab', label: 'GitLab Repo', icon: Code },
      { type: 'storage', subtype: 'bitbucket', label: 'Bitbucket', icon: Code }
    ]
  },
  {
    id: 'analytics',
    name: 'Analytics & Tracking',
    color: professionalColors.analytics.primary,
    description: 'Análisis y métricas',
    nodes: [
      { type: 'analytics', subtype: 'google_analytics', label: 'Google Analytics', icon: BarChart },
      { type: 'analytics', subtype: 'ga4_event', label: 'GA4 Event', icon: Activity },
      { type: 'analytics', subtype: 'mixpanel', label: 'Mixpanel', icon: PieChart },
      { type: 'analytics', subtype: 'amplitude', label: 'Amplitude', icon: TrendingUp },
      { type: 'analytics', subtype: 'segment', label: 'Segment Track', icon: Target },
      { type: 'analytics', subtype: 'hotjar', label: 'Hotjar Event', icon: Activity },
      { type: 'analytics', subtype: 'posthog', label: 'PostHog', icon: BarChart },
      { type: 'analytics', subtype: 'heap', label: 'Heap Analytics', icon: PieChart },
      { type: 'analytics', subtype: 'facebook_pixel', label: 'Facebook Pixel', icon: Facebook },
      { type: 'analytics', subtype: 'google_tag', label: 'Google Tag Manager', icon: Tag },
      { type: 'analytics', subtype: 'custom_event', label: 'Custom Event', icon: Zap },
      { type: 'analytics', subtype: 'ab_test', label: 'A/B Test', icon: Shuffle }
    ]
  },
  {
    id: 'marketing',
    name: 'Marketing & Advertising',
    color: professionalColors.marketing.primary,
    description: 'Marketing y publicidad',
    nodes: [
      { type: 'marketing', subtype: 'facebook_ads', label: 'Facebook Ads', icon: Facebook },
      { type: 'marketing', subtype: 'google_ads', label: 'Google Ads', icon: Search },
      { type: 'marketing', subtype: 'instagram_ads', label: 'Instagram Ads', icon: Instagram },
      { type: 'marketing', subtype: 'linkedin_ads', label: 'LinkedIn Ads', icon: Linkedin },
      { type: 'marketing', subtype: 'twitter_ads', label: 'Twitter Ads', icon: Twitter },
      { type: 'marketing', subtype: 'mailchimp', label: 'Mailchimp Campaign', icon: Mail },
      { type: 'marketing', subtype: 'sendgrid', label: 'SendGrid Email', icon: Mail },
      { type: 'marketing', subtype: 'activecampaign', label: 'ActiveCampaign', icon: Zap },
      { type: 'marketing', subtype: 'convertkit', label: 'ConvertKit', icon: Mail },
      { type: 'marketing', subtype: 'hubspot_email', label: 'HubSpot Email', icon: Mail },
      { type: 'marketing', subtype: 'klaviyo', label: 'Klaviyo', icon: Mail },
      { type: 'marketing', subtype: 'brevo', label: 'Brevo (Sendinblue)', icon: Mail },
      { type: 'marketing', subtype: 'tiktok_ads', label: 'TikTok Ads', icon: Video },
      { type: 'marketing', subtype: 'youtube_ads', label: 'YouTube Ads', icon: Youtube },
      { type: 'marketing', subtype: 'pinterest_ads', label: 'Pinterest Ads', icon: Image }
    ]
  },
  {
    id: 'logic',
    name: 'Logic & Flow Control',
    color: professionalColors.logic.primary,
    description: 'Control de flujo y lógica',
    nodes: [
      { type: 'logic', subtype: 'condition', label: 'If Condition', icon: GitBranch },
      { type: 'logic', subtype: 'switch', label: 'Switch Case', icon: Shuffle },
      { type: 'logic', subtype: 'router', label: 'Router', icon: GitBranch },
      { type: 'logic', subtype: 'loop', label: 'Loop/Iterate', icon: Repeat },
      { type: 'logic', subtype: 'for_each', label: 'For Each', icon: Repeat },
      { type: 'logic', subtype: 'while', label: 'While Loop', icon: Repeat },
      { type: 'logic', subtype: 'filter', label: 'Filter Array', icon: Filter },
      { type: 'logic', subtype: 'sort', label: 'Sort Array', icon: ArrowUp },
      { type: 'logic', subtype: 'merge', label: 'Merge Data', icon: Copy },
      { type: 'logic', subtype: 'split', label: 'Split Data', icon: ArrowRight },
      { type: 'logic', subtype: 'aggregate', label: 'Aggregate', icon: Plus },
      { type: 'logic', subtype: 'limit', label: 'Limit Results', icon: Minus },
      { type: 'logic', subtype: 'delay', label: 'Delay/Wait', icon: Clock },
      { type: 'logic', subtype: 'error_handler', label: 'Error Handler', icon: AlertCircle },
      { type: 'logic', subtype: 'retry', label: 'Retry Logic', icon: Repeat }
    ]
  },
  {
    id: 'transform',
    name: 'Data Transformation',
    color: professionalColors.transform.primary,
    description: 'Transformación de datos',
    nodes: [
      { type: 'transform', subtype: 'json_parse', label: 'JSON Parser', icon: Code },
      { type: 'transform', subtype: 'json_stringify', label: 'JSON Stringify', icon: FileText },
      { type: 'transform', subtype: 'xml_parse', label: 'XML Parser', icon: Code },
      { type: 'transform', subtype: 'csv_parse', label: 'CSV Parser', icon: FileText },
      { type: 'transform', subtype: 'data_mapper', label: 'Data Mapper', icon: GitBranch },
      { type: 'transform', subtype: 'text_extract', label: 'Text Extract (Regex)', icon: Search },
      { type: 'transform', subtype: 'text_replace', label: 'Text Replace', icon: Edit },
      { type: 'transform', subtype: 'hash', label: 'Hash Generator', icon: Lock },
      { type: 'transform', subtype: 'encrypt', label: 'Encrypt Data', icon: Lock },
      { type: 'transform', subtype: 'decrypt', label: 'Decrypt Data', icon: Unlock },
      { type: 'transform', subtype: 'base64', label: 'Base64 Encode/Decode', icon: Code },
      { type: 'transform', subtype: 'url_encode', label: 'URL Encode', icon: Link }
    ]
  },
  {
    id: 'action',
    name: 'Actions & Automation',
    color: professionalColors.action.primary,
    description: 'Acciones y automatización',
    nodes: [
      { type: 'action', subtype: 'send_message', label: 'Send Message', icon: Send },
      { type: 'action', subtype: 'create_ticket', label: 'Create Ticket', icon: Flag },
      { type: 'action', subtype: 'tag_contact', label: 'Tag Contact', icon: Tag },
      { type: 'action', subtype: 'update_contact', label: 'Update Contact', icon: UserCheck },
      { type: 'action', subtype: 'create_task', label: 'Create Task', icon: Check },
      { type: 'action', subtype: 'send_email', label: 'Send Email', icon: Mail },
      { type: 'action', subtype: 'log_event', label: 'Log Event', icon: FileText },
      { type: 'action', subtype: 'webhook_call', label: 'Call Webhook', icon: Webhook },
      { type: 'action', subtype: 'http_request', label: 'HTTP Request', icon: Globe },
      { type: 'action', subtype: 'graphql', label: 'GraphQL Query', icon: Code },
      { type: 'action', subtype: 'run_script', label: 'Run JavaScript', icon: Terminal },
      { type: 'action', subtype: 'python_script', label: 'Run Python', icon: Code },
      { type: 'action', subtype: 'schedule_action', label: 'Schedule Action', icon: Calendar },
      { type: 'action', subtype: 'trigger_flow', label: 'Trigger Another Flow', icon: Zap },
      { type: 'action', subtype: 'stop_flow', label: 'Stop Flow', icon: X }
    ]
  }
];

const NodePalette = ({ onNodeClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);

  const totalNodesCount = nodeCategories.reduce((acc, cat) => acc + cat.nodes.length, 0);
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const onDragStart = (event, node) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(node));
    event.dataTransfer.effectAllowed = 'move';
  };

  const filteredCategories = nodeCategories
    .map((category) => {
      const displayName = i18n.t(`nodePalette.categories.${category.id}.name`, {
        defaultValue: category.name,
      });
      const displayDescription = i18n.t(`nodePalette.categories.${category.id}.description`, {
        defaultValue: category.description,
      });

      const nodes = category.nodes
        .map((node) => ({
          ...node,
          displayLabel: i18n.t(`nodePalette.nodes.${node.type}.${node.subtype}`, {
            defaultValue: node.label,
          }),
        }))
        .filter((node) => {
          if (!normalizedSearch) return true;
          return (
            node.displayLabel.toLowerCase().includes(normalizedSearch) ||
            node.subtype.toLowerCase().includes(normalizedSearch)
          );
        });

      return { ...category, displayName, displayDescription, nodes };
    })
    .filter((category) => category.nodes.length > 0);

  return (
    <div style={{
      width: 320,
      background: 'linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)',
      borderRight: '1px solid #e5e7eb',
      padding: '20px 16px',
      overflowY: 'auto',
      height: '100%',
      boxShadow: '2px 0 8px rgba(0,0,0,0.05)'
    }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{
          fontSize: 20,
          fontWeight: 700,
          marginBottom: 8,
          color: '#111827',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          {i18n.t("nodePalette.title")}
        </h2>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
          {i18n.t("nodePalette.nodeCount", { count: totalNodesCount })}
        </p>

        <input
          type="text"
          placeholder={i18n.t("nodePalette.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '2px solid #e5e7eb',
            borderRadius: 8,
            fontSize: 13,
            outline: 'none',
            transition: 'border-color 0.2s',
            fontFamily: 'inherit'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
        />
      </div>

      {filteredCategories.map((category, categoryIndex) => {
        const isExpanded = expandedCategory === categoryIndex || searchTerm;
        const nodeCount = category.nodes.length;

        return (
          <div key={category.id} style={{ marginBottom: 20 }}>
            <div
              onClick={() => setExpandedCategory(isExpanded && !searchTerm ? null : categoryIndex)}
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: category.color,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: `${category.color}15`,
                borderRadius: 8,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${category.color}25`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `${category.color}15`;
              }}
            >
              <span>{category.displayName}</span>
              <span style={{
                background: category.color,
                color: 'white',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 10,
                fontWeight: 600
              }}>
                {nodeCount}
              </span>
            </div>

            <p style={{
              fontSize: 11,
              color: '#6b7280',
              marginBottom: 12,
              paddingLeft: 12,
              fontStyle: 'italic'
            }}>
              {category.displayDescription}
            </p>

            <div style={{
              maxHeight: isExpanded ? '2000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out'
            }}>
              {category.nodes.map((node) => {
                const Icon = node.icon;
                return (
                  <div
                    key={node.subtype}
                    draggable
                    onClick={() => onNodeClick && onNodeClick(node)}
                    onDragStart={(e) => onDragStart(e, node)}
                    style={{
                      padding: '12px 14px',
                      marginBottom: 8,
                      background: 'white',
                      border: `2px solid ${category.color}40`,
                      borderRadius: 10,
                      cursor: 'grab',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateX(6px) scale(1.02)';
                      e.currentTarget.style.boxShadow = `0 8px 20px ${category.color}30`;
                      e.currentTarget.style.borderColor = category.color;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateX(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                      e.currentTarget.style.borderColor = `${category.color}40`;
                    }}
                  >
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: `linear-gradient(135deg, ${category.color}20, ${category.color}10)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon size={18} color={category.color} strokeWidth={2.5} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#111827',
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {node.displayLabel}
                      </span>
                      <span style={{
                        fontSize: 10,
                        color: '#9ca3af',
                        display: 'block',
                        marginTop: 2
                      }}>
                        {node.subtype}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {filteredCategories.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#9ca3af'
        }}>
          <Search size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
          <p style={{ fontSize: 14 }}>{i18n.t("nodePalette.noResults.title")}</p>
          <p style={{ fontSize: 12, marginTop: 8 }}>{i18n.t("nodePalette.noResults.subtitle")}</p>
        </div>
      )}
    </div>
  );
};

export default NodePalette;
